import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import {
  MINIMUM_REACTION_TIME_MS,
  PROTOCOL_VERSION,
  SESSION_TRIALS,
  createId,
  getEnvironmentConditionId,
  isFrameDrop,
  visualDegreesToPixels,
} from '../core';
import type {
  CalibrationProfile,
  EnvironmentSnapshot,
  ModuleId,
  SessionCompletionPayload,
  SessionMode,
  SessionRecord,
  TrialInvalidReason,
  TrialPlan,
  TrialResult,
} from '../core/types';
import { getTrainingModule, TRAINING_MODULES } from './modules';
import { vehicleAssetUrl, type CentralVehicleType } from './modules/vehicle-assets';
import {
  isAssessmentMode,
  responseIsCorrect,
  type AnyRuntimeTrainingModule,
  type ResponseOption,
  type ResponseVisual,
  type TrainingFrameContext,
} from './runtime';
import { summarizeModuleResults } from './summarize';
import { buildTrialSpecifications, type TrialSpecification } from './trial-queue';
import './training.css';

type Phase = 'module-intro' | 'fixation' | 'active' | 'feedback' | 'paused' | 'saving' | 'complete';

interface UiState {
  phase: Phase;
  moduleIndex: number;
  trialIndex: number;
  plan?: TrialPlan;
  selected: string[];
  responseReady: boolean;
  lastResult?: TrialResult;
  pauseReason?: string;
}

export interface TrainingSessionProps {
  mode: SessionMode;
  calibration?: CalibrationProfile;
  environment: EnvironmentSnapshot;
  fatigue: 1 | 2 | 3 | 4 | 5;
  correctionConfirmed: boolean;
  onStart?: (session: SessionRecord) => void | Promise<void>;
  onTrial?: (trial: TrialResult) => void | Promise<void>;
  onComplete: (payload: SessionCompletionPayload) => void | Promise<void>;
  onAbort?: (session: SessionRecord, trials: TrialResult[]) => void | Promise<void>;
  onExit: () => void;
  moduleIds?: ModuleId[];
  initialDifficulties?: Partial<Record<ModuleId, number>>;
  trialsPerModule?: number;
  requestFullscreen?: boolean;
  resume?: { session: SessionRecord; trials: TrialResult[] };
}

function drawFixation(canvas: HTMLCanvasElement, dpr: number): void {
  const rect = canvas.getBoundingClientRect();
  const width = Math.max(1, Math.round(rect.width));
  const height = Math.max(1, Math.round(rect.height));
  if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
  }
  const context = canvas.getContext('2d');
  if (!context) return;
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  context.fillStyle = '#030806';
  context.fillRect(0, 0, width, height);
  context.strokeStyle = '#a9c5bd';
  context.lineWidth = 2;
  context.beginPath();
  context.moveTo(width / 2 - 10, height / 2);
  context.lineTo(width / 2 + 10, height / 2);
  context.moveTo(width / 2, height / 2 - 10);
  context.lineTo(width / 2, height / 2 + 10);
  context.stroke();
}

function canonicalMulti(values: string[]): string {
  return [...values].sort((left, right) => Number(left) - Number(right)).join(',');
}

function serializeMulti(module: AnyRuntimeTrainingModule, values: readonly string[]): string {
  return module.serializeSelections?.(values) ?? canonicalMulti([...values]);
}

const POSITION_BUTTONS = Array.from({ length: 8 }, (_, index) => {
  const angle = -Math.PI / 2 + index * Math.PI / 4;
  return {
    xPercent: 50 + Math.cos(angle) * 26,
    yPercent: 49 + Math.sin(angle) * 31,
  };
});

function decorateResponseOption(option: ResponseOption): ResponseOption {
  const vehicleMatch = /^vehicle:(convertible|box-truck|sedan|suv|hatchback)$/.exec(option.value);
  if (vehicleMatch) {
    return { ...option, visual: `vehicle-${vehicleMatch[1]}` as ResponseVisual };
  }
  if (option.visual || option.position) return option;
  if (option.value.includes('contracting')) return { ...option, visual: 'sweep-inward' };
  if (option.value.includes('expanding')) return { ...option, visual: 'sweep-outward' };
  if (option.value === 'central:vertical') return { ...option, visual: 'orientation-vertical' };
  if (option.value === 'central:horizontal') return { ...option, visual: 'orientation-horizontal' };
  const positionMatch = /^position:([1-8])$/.exec(option.value);
  if (positionMatch) {
    const position = POSITION_BUTTONS[Number(positionMatch[1]) - 1];
    return { ...option, visual: 'location', position };
  }
  return option;
}

function responseVisualGlyph(visual: ResponseVisual | undefined): string | undefined {
  switch (visual) {
    case 'arrow-up': return '↑';
    case 'arrow-right': return '→';
    case 'arrow-down': return '↓';
    case 'arrow-left': return '←';
    case 'sweep-inward': return '⇢⇠';
    case 'sweep-outward': return '⇠⇢';
    case 'orientation-vertical': return '▥';
    case 'orientation-horizontal': return '▤';
    case 'location': return '●';
    default: return undefined;
  }
}

function VehicleIllustration({ visual }: { visual: ResponseVisual }) {
  const type = visual.replace('vehicle-', '') as CentralVehicleType;
  return <img className="training-vehicle-illustration" src={vehicleAssetUrl(type)} alt="" />;
}

function ResponseVisualIcon({ visual }: { visual: ResponseVisual | undefined }) {
  if (!visual) return null;
  if (visual.startsWith('vehicle-')) {
    return <span className={`training-option-visual visual-${visual}`} aria-hidden="true"><VehicleIllustration visual={visual} /></span>;
  }
  const glyph = responseVisualGlyph(visual);
  return glyph
    ? <span className={`training-option-visual visual-${visual}`} aria-hidden="true">{glyph}</span>
    : null;
}

function pointerFirstInstructions(module: AnyRuntimeTrainingModule): readonly string[] {
  if (module.id === 'central-peripheral') return [
    '中央の車種と、周辺に一瞬出る白い標識の位置を同時に覚えます。記号は黒い丸かばつです。',
    '刺激とマスクが消えたら、まず車種ボタン、続けて黒い丸の標識があった画面位置を選びます。',
    '正答すると提示が短くなり、標識が遠く・背景が複雑になり、ばつの妨害標識が増えます。',
  ];
  return module.instructions;
}

function createSessionRecord(
  id: string,
  startedAt: string,
  mode: SessionMode,
  moduleIds: ModuleId[],
  environmentId: string,
  environmentConditionId: string,
  fatigue: 1 | 2 | 3 | 4 | 5,
  correctionConfirmed: boolean,
  trials: TrialResult[],
  status: SessionRecord['status'],
): SessionRecord {
  return {
    id,
    startedAt,
    completedAt: status === 'completed' ? new Date().toISOString() : undefined,
    mode,
    protocolVersion: PROTOCOL_VERSION,
    moduleIds,
    status,
    environmentId,
    environmentConditionId,
    fatigue,
    correctionConfirmed,
    trialIds: trials.map(({ id: trialId }) => trialId),
  };
}

export function TrainingSession({
  mode,
  calibration,
  environment,
  fatigue,
  correctionConfirmed,
  onStart,
  onTrial,
  onComplete,
  onAbort,
  onExit,
  moduleIds,
  initialDifficulties: storedDifficulties,
  trialsPerModule,
  resume: resumeData,
}: TrainingSessionProps) {
  const sequence = useMemo(
    () => (moduleIds?.length ? moduleIds.map(getTrainingModule) : [...TRAINING_MODULES]),
    [moduleIds],
  );
  const trialTotal = trialsPerModule ?? SESSION_TRIALS[mode];
  const sessionId = useRef(resumeData?.session.id ?? createId('session'));
  const sessionStartedAt = useRef(resumeData?.session.startedAt ?? new Date().toISOString());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const resultsRef = useRef<TrialResult[]>([...(resumeData?.trials ?? [])]);
  const initialDifficulties: Record<ModuleId, number> = {
    'read-switch': mode === 'familiarization' ? 1.5 : storedDifficulties?.['read-switch'] ?? 3.5,
    'central-peripheral': mode === 'familiarization' ? 1.5 : storedDifficulties?.['central-peripheral'] ?? 3.5,
    'player-track': mode === 'familiarization' ? 1.5 : storedDifficulties?.['player-track'] ?? 3.5,
    'moving-target': mode === 'familiarization' ? 1.5 : storedDifficulties?.['moving-target'] ?? 3.5,
  };
  if (mode === 'training' && resumeData?.trials.length) {
    for (const module of sequence) {
      const history: TrialResult[] = [];
      for (const trial of resumeData.trials.filter((item) => item.moduleId === module.id)) {
        history.push(trial);
        if (trial.valid) initialDifficulties[module.id] = module.adaptDifficulty(initialDifficulties[module.id], history);
      }
    }
  }
  const difficultyRef = useRef<Record<ModuleId, number>>(initialDifficulties);
  const phaseStartedAt = useRef(performance.now());
  const stimulusOnsetAt = useRef(0);
  const responseOnsetAt = useRef(0);
  const trialStartedAt = useRef(new Date().toISOString());
  const lastFrameAt = useRef<number | undefined>(undefined);
  const invalidReason = useRef<TrialInvalidReason | undefined>(undefined);
  const pausedPhase = useRef<Phase>('module-intro');
  const pausedAt = useRef(0);
  const completed = useRef(false);
  const startAttempted = useRef(false);
  const finishing = useRef(false);
  const pendingResult = useRef<TrialResult | undefined>(undefined);
  const selectionAnticipated = useRef(false);
  const firstSelectionAt = useRef<number | undefined>(undefined);
  const resumedTrialCount = Math.min(resumeData?.trials.length ?? 0, sequence.length * trialTotal);
  const initialModuleIndex = Math.min(sequence.length - 1, Math.floor(resumedTrialCount / trialTotal));
  const initialTrialIndex = resumedTrialCount >= sequence.length * trialTotal ? trialTotal - 1 : resumedTrialCount % trialTotal;
  const [ui, setUiState] = useState<UiState>({
    phase: 'module-intro', moduleIndex: initialModuleIndex, trialIndex: initialTrialIndex, selected: [], responseReady: false,
  });
  const [startPersisted, setStartPersisted] = useState(Boolean(resumeData));
  const [storageError, setStorageError] = useState<string>();
  const uiRef = useRef(ui);
  const setUi = useCallback((next: UiState | ((current: UiState) => UiState)) => {
    setUiState((current) => {
      const resolved = typeof next === 'function' ? next(current) : next;
      uiRef.current = resolved;
      return resolved;
    });
  }, []);

  const activeModule = sequence[ui.moduleIndex] ?? sequence[0];
  const overallTrial = ui.moduleIndex * trialTotal + ui.trialIndex;
  const overallTotal = sequence.length * trialTotal;
  const specifications = useMemo<Record<ModuleId, TrialSpecification[]>>(() => Object.fromEntries(
    sequence.map((module) => [module.id, buildTrialSpecifications(module.id, mode, trialTotal, sessionId.current)]),
  ) as Record<ModuleId, TrialSpecification[]>, [mode, sequence, trialTotal]);

  const currentSessionRecord = useCallback((status: SessionRecord['status'], trials = resultsRef.current) => createSessionRecord(
    sessionId.current,
    sessionStartedAt.current,
    mode,
    sequence.map(({ id }) => id),
    environment.id,
    getEnvironmentConditionId(environment),
    fatigue,
    correctionConfirmed,
    trials,
    status,
  ), [correctionConfirmed, environment, fatigue, mode, sequence]);

  const persistStart = useCallback(async () => {
    if (!onStart) {
      setStartPersisted(true);
      return;
    }
    setStorageError(undefined);
    try {
      await onStart(currentSessionRecord('in-progress'));
      setStartPersisted(true);
    } catch {
      setStartPersisted(false);
      setStorageError('ローカル保存を開始できませんでした。空き容量とブラウザ設定を確認してください。');
    }
  }, [currentSessionRecord, onStart]);

  useEffect(() => {
    if (resumeData || startAttempted.current) return;
    startAttempted.current = true;
    void persistStart();
  }, [persistStart, resumeData]);

  const completeSession = useCallback(async (trials: TrialResult[]) => {
    if (completed.current) return;
    setStorageError(undefined);
    setUi((current) => ({ ...current, phase: 'saving', responseReady: false }));
    const session = currentSessionRecord('completed', trials);
    const summaries = sequence.map((module) => summarizeModuleResults(module, trials));
    try {
      await onComplete({ session, trials, summaries });
      completed.current = true;
      setUi((current) => ({ ...current, phase: 'complete', responseReady: false }));
    } catch {
      pausedPhase.current = 'saving';
      setStorageError('完了記録を保存できませんでした。生試行は端末内に残っています。');
      setUi((current) => ({ ...current, phase: 'paused', pauseReason: '保存に失敗しました。再試行してください。', responseReady: false }));
    }
  }, [currentSessionRecord, onComplete, sequence, setUi]);

  useEffect(() => {
    if (startPersisted && resumedTrialCount >= overallTotal && !completed.current) {
      void completeSession(resultsRef.current);
    }
  }, [completeSession, overallTotal, resumedTrialCount, startPersisted]);

  const startTrial = useCallback((moduleIndex: number, trialIndex: number) => {
    const module = sequence[moduleIndex];
    if (!module) return;
    const specification = specifications[module.id]?.[trialIndex];
    if (!specification) return;
    const seed = specification.seed;
    const difficulty = isAssessmentMode(mode) ? specification.difficulty : difficultyRef.current[module.id];
    const plan = module.createTrial(seed, difficulty, mode);
    invalidReason.current = undefined;
    selectionAnticipated.current = false;
    firstSelectionAt.current = undefined;
    lastFrameAt.current = undefined;
    phaseStartedAt.current = performance.now();
    trialStartedAt.current = new Date().toISOString();
    setUi({ phase: 'fixation', moduleIndex, trialIndex, plan, selected: [], responseReady: false });
  }, [mode, sequence, setUi, specifications]);

  const advance = useCallback(async () => {
    const current = uiRef.current;
    if (current.trialIndex + 1 < trialTotal) {
      startTrial(current.moduleIndex, current.trialIndex + 1);
      return;
    }
    if (current.moduleIndex + 1 < sequence.length) {
      phaseStartedAt.current = performance.now();
      setUi({ phase: 'module-intro', moduleIndex: current.moduleIndex + 1, trialIndex: 0, selected: [], responseReady: false });
      return;
    }
    await completeSession(resultsRef.current);
  }, [completeSession, sequence.length, setUi, startTrial, trialTotal]);

  const commitResult = useCallback((result: TrialResult, module: AnyRuntimeTrainingModule) => {
    if (!resultsRef.current.some((item) => item.id === result.id)) {
      resultsRef.current = [...resultsRef.current, result];
    }
    if (mode === 'training' && result.valid) {
      const recent = resultsRef.current.filter((trial) => trial.moduleId === module.id);
      difficultyRef.current[module.id] = module.adaptDifficulty(difficultyRef.current[module.id], recent);
    }
    phaseStartedAt.current = performance.now();
    pendingResult.current = undefined;
    finishing.current = false;
    setStorageError(undefined);
    setUi((state) => ({ ...state, phase: 'feedback', responseReady: false, lastResult: result }));
  }, [mode, setUi]);

  const persistResult = useCallback(async (result: TrialResult, module: AnyRuntimeTrainingModule) => {
    try {
      await onTrial?.(result);
      commitResult(result, module);
    } catch {
      pendingResult.current = result;
      finishing.current = false;
      pausedPhase.current = 'active';
      setStorageError('この試行を保存できませんでした。保存を再試行するまで次へ進みません。');
      setUi((state) => ({ ...state, phase: 'paused', responseReady: false, lastResult: result, pauseReason: 'ローカル保存に失敗しました。' }));
    }
  }, [commitResult, onTrial, setUi]);

  const finishTrial = useCallback(async (response?: string, forcedInvalid?: TrialInvalidReason) => {
    const current = uiRef.current;
    const plan = current.plan;
    const module = sequence[current.moduleIndex];
    if (finishing.current || !plan || !module || (current.phase !== 'active' && current.phase !== 'fixation')) return;
    finishing.current = true;
    const now = performance.now();
    const reason = forcedInvalid ?? invalidReason.current;
    const partialResponse = response ?? (
      module.responseKind === 'multi-select' && current.selected.length
        ? canonicalMulti(current.selected)
        : undefined
    );
    const unconfirmedPartial = response === undefined && partialResponse !== undefined;
    const reactionAt = module.responseKind === 'multi-select'
      ? firstSelectionAt.current ?? now
      : now;
    const rt = partialResponse === undefined || unconfirmedPartial || responseOnsetAt.current === 0
      ? undefined
      : reactionAt - responseOnsetAt.current;
    const anticipation = rt !== undefined && rt < MINIMUM_REACTION_TIME_MS;
    const multiSelectAnticipation = module.responseKind === 'multi-select' && selectionAnticipated.current;
    const finalReason = reason ?? (anticipation || multiSelectAnticipation ? 'anticipation' : undefined);
    const timeoutResponse = partialResponse === undefined ? module.getTimeoutResponse?.(plan) : undefined;
    const finalResponse = partialResponse ?? timeoutResponse;
    const omission = finalResponse === undefined;
    const result: TrialResult = {
      id: createId('trial'),
      sessionId: sessionId.current,
      trialId: plan.id,
      moduleId: module.id,
      protocolVersion: plan.protocolVersion,
      seed: plan.seed,
      stimulusId: plan.stimulusId,
      startedAt: trialStartedAt.current,
      stimulusOnsetMs: stimulusOnsetAt.current,
      responseAtMs: finalResponse === undefined || unconfirmedPartial ? undefined : performance.timeOrigin + now,
      response: finalResponse,
      correctResponse: plan.correctResponse,
      correct: !unconfirmedPartial && responseIsCorrect(plan, finalResponse),
      reactionTimeMs: timeoutResponse ? undefined : rt,
      difficulty: plan.difficulty,
      valid: !finalReason,
      invalidReason: finalReason,
      environmentId: environment.id,
      metadata: {
        ...(module.getResultMetadata?.(plan, finalResponse) ?? {}),
        sessionMode: mode,
        assessment: isAssessmentMode(mode),
        omission,
        firstSelectionLatencyMs: firstSelectionAt.current === undefined || responseOnsetAt.current === 0
          ? null
          : firstSelectionAt.current - responseOnsetAt.current,
      },
    };
    setUi((state) => ({ ...state, responseReady: false }));
    await persistResult(result, module);
  }, [environment.id, mode, persistResult, sequence, setUi]);

  useEffect(() => {
    if (ui.phase !== 'feedback') return;
    const module = sequence[ui.moduleIndex];
    const showFeedback = module?.showTrainingFeedback !== false;
    const delay = (mode === 'training' || mode === 'familiarization') && showFeedback ? 1900 : 420;
    const handle = window.setTimeout(() => { void advance(); }, delay);
    return () => window.clearTimeout(handle);
  }, [advance, mode, sequence, ui.moduleIndex, ui.phase, ui.lastResult?.id]);

  useEffect(() => {
    if (!['fixation', 'active', 'feedback'].includes(ui.phase)) return;
    let handle = 0;
    const render = (now: number) => {
      const current = uiRef.current;
      const canvas = canvasRef.current;
      const plan = current.plan;
      const module = sequence[current.moduleIndex];
      if (!canvas || !plan || !module) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
      }
      if (current.phase === 'fixation') {
        drawFixation(canvas, dpr);
        if (now - phaseStartedAt.current >= plan.timings.fixationMs) {
          stimulusOnsetAt.current = performance.timeOrigin + now;
          const onsetOffset = module.getResponseOnsetMs?.(plan) ?? plan.timings.stimulusMs;
          responseOnsetAt.current = now + onsetOffset;
          phaseStartedAt.current = now;
          setUi((state) => ({ ...state, phase: 'active' }));
        }
      } else {
        const context = canvas.getContext('2d');
        if (!context) return;
        const elapsed = now - phaseStartedAt.current;
        const pixelsPerDegree = calibration
          ? visualDegreesToPixels(1, calibration.viewingDistanceCm, calibration.pixelsPerCentimeter)
          : width / 90;
        const frame = {
          now,
          elapsed,
          width,
          height,
          devicePixelRatio: dpr,
          pixelsPerDegree,
          calibration,
          selectedResponses: current.selected,
        };
        if (current.phase === 'feedback' && module.renderFeedback && current.lastResult && (mode === 'training' || mode === 'familiarization')) {
          module.renderFeedback(context, frame, plan, current.lastResult.response);
        } else if (current.phase === 'feedback') {
          drawFixation(canvas, dpr);
        } else {
          module.render(context, frame, plan);
          const responseOnset = module.getResponseOnsetMs?.(plan) ?? plan.timings.stimulusMs;
          if (!current.responseReady && elapsed >= responseOnset) setUi((state) => ({ ...state, responseReady: true }));
          if (lastFrameAt.current !== undefined) {
            const interval = now - lastFrameAt.current;
            if (isFrameDrop(interval, environment.frameIntervalMedianMs) && elapsed <= responseOnset + 250) {
              invalidReason.current ??= 'frame-drop';
            }
          }
          if (elapsed >= responseOnset + plan.timings.responseWindowMs) void finishTrial();
        }
      }
      lastFrameAt.current = now;
      handle = requestAnimationFrame(render);
    };
    handle = requestAnimationFrame(render);
    return () => cancelAnimationFrame(handle);
  }, [calibration, environment.frameIntervalMedianMs, finishTrial, mode, sequence, setUi, ui.phase, ui.plan?.id]);

  const pause = useCallback((reason = '一時停止', manual = false) => {
    const current = uiRef.current;
    if (current.phase === 'paused' || current.phase === 'complete' || current.phase === 'saving') return;
    if (manual && (current.phase === 'fixation' || current.phase === 'active')) {
      invalidReason.current ??= 'manual-pause';
    }
    const pauseReason = manual && current.phase !== 'fixation' && current.phase !== 'active'
      ? '手動で一時停止しました。'
      : reason;
    pausedPhase.current = current.phase;
    pausedAt.current = performance.now();
    setUi((state) => ({ ...state, phase: 'paused', pauseReason, responseReady: false }));
  }, [setUi]);

  const environmentStillMatches = useCallback(() =>
    window.innerWidth === environment.viewportWidth
      && window.innerHeight === environment.viewportHeight
      && Math.abs(window.devicePixelRatio - environment.devicePixelRatio) <= 0.05,
  [environment.devicePixelRatio, environment.viewportHeight, environment.viewportWidth]);

  const resume = useCallback(async () => {
    if (storageError) return;
    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
    } catch {
      setUi((state) => ({ ...state, pauseReason: '全画面表示を許可してから再開してください。' }));
      return;
    }
    if (!environmentStillMatches()) {
      setUi((state) => ({ ...state, pauseReason: '画面条件が開始時から変わっています。このセッションを中止し、準備画面から再診断してください。' }));
      return;
    }
    const delta = performance.now() - pausedAt.current;
    phaseStartedAt.current += delta;
    if (responseOnsetAt.current) responseOnsetAt.current += delta;
    lastFrameAt.current = undefined;
    setUi((state) => ({ ...state, phase: pausedPhase.current, pauseReason: undefined }));
  }, [environmentStillMatches, setUi, storageError]);

  useEffect(() => {
    const invalidateAndPause = (reason: TrialInvalidReason, message: string) => {
      const current = uiRef.current;
      if (current.phase === 'feedback') {
        pause('表示環境が変わったため、次の試行前に停止しました。保存済みの直前試行は変更しません。');
        return;
      }
      if (current.phase !== 'fixation' && current.phase !== 'active') return;
      invalidReason.current ??= reason;
      pause(message);
    };
    const onVisibility = () => { if (document.hidden) invalidateAndPause('visibility-hidden', 'タブが非表示になったため、この試行は無効です。'); };
    const onBlur = () => invalidateAndPause('focus-lost', 'フォーカスが外れたため、この試行は無効です。');
    const onResize = () => invalidateAndPause('viewport-changed', '画面サイズが変わったため、この試行は無効です。');
    const onFullscreen = () => { if (!document.fullscreenElement) invalidateAndPause('fullscreen-exited', '全画面表示が解除されたため、この試行は無効です。'); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('resize', onResize);
    document.addEventListener('fullscreenchange', onFullscreen);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('fullscreenchange', onFullscreen);
    };
  }, [pause]);

  const submitOption = useCallback((value: string, submittedAt = performance.now()) => {
    const current = uiRef.current;
    const module = sequence[current.moduleIndex];
    const plan = current.plan;
    if (!module || !plan || current.phase !== 'active' || finishing.current) return;
    const deadline = responseOnsetAt.current + plan.timings.responseWindowMs;
    if (responseOnsetAt.current && submittedAt > deadline) {
      void finishTrial();
      return;
    }
    if (submittedAt < responseOnsetAt.current) {
      void finishTrial(value, 'anticipation');
      return;
    }
    if (module.responseKind === 'single') {
      void finishTrial(value);
      return;
    }

    firstSelectionAt.current ??= submittedAt;
    if (submittedAt - responseOnsetAt.current < MINIMUM_REACTION_TIME_MS) {
      selectionAnticipated.current = true;
    }
    const selectionLimit = module.selectionCount ?? 3;
    const nextSelected = current.selected.includes(value)
      ? current.selected.filter((selected) => selected !== value)
      : current.selected.length < selectionLimit
        ? [...current.selected, value]
        : current.selected;
    if (module.autoSubmitMultiSelect && nextSelected.length === selectionLimit) {
      void finishTrial(
        serializeMulti(module, nextSelected),
        selectionAnticipated.current ? 'anticipation' : undefined,
      );
      return;
    }
    setUi((state) => ({ ...state, selected: nextSelected }));
  }, [finishTrial, sequence, setUi]);

  const confirmSelections = useCallback((submittedAt = performance.now()) => {
    const current = uiRef.current;
    const module = sequence[current.moduleIndex];
    const plan = current.plan;
    if (!module || !plan || current.phase !== 'active' || module.responseKind !== 'multi-select') return;
    if (responseOnsetAt.current && submittedAt > responseOnsetAt.current + plan.timings.responseWindowMs) {
      void finishTrial();
      return;
    }
    if (current.selected.length !== (module.selectionCount ?? 3)) return;
    void finishTrial(
      serializeMulti(module, current.selected),
      selectionAnticipated.current || submittedAt < responseOnsetAt.current ? 'anticipation' : undefined,
    );
  }, [finishTrial, sequence]);

  const answerFromKey = useCallback((event: KeyboardEvent) => {
    const current = uiRef.current;
    if (event.key === 'Escape') {
      event.preventDefault();
      if (current.phase === 'paused') void resume();
      else pause('手動で一時停止しました。この試行は連続提示が途切れたため無効です。', true);
      return;
    }
    const module = sequence[current.moduleIndex];
    const plan = current.plan;
    if (!module || !plan || current.phase !== 'active' || event.repeat) return;
    if (event.key === 'Enter' && module.responseKind === 'multi-select') {
      if (current.selected.length !== (module.selectionCount ?? 3)) return;
      event.preventDefault();
      confirmSelections();
      return;
    }
    const option = module
      .getResponseOptions(plan, current.selected)
      .find((candidate) => candidate.keys?.includes(event.key));
    if (!option) return;
    event.preventDefault();
    submitOption(option.value);
  }, [confirmSelections, pause, resume, sequence, submitOption]);

  useEffect(() => {
    window.addEventListener('keydown', answerFromKey, { capture: true });
    return () => window.removeEventListener('keydown', answerFromKey, { capture: true });
  }, [answerFromKey]);

  const answerFromCanvas = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    const current = uiRef.current;
    const module = sequence[current.moduleIndex];
    const plan = current.plan;
    const canvas = canvasRef.current;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (
      !canvas || !module?.getCanvasResponse || !plan || current.phase !== 'active'
    ) return;
    if (!current.responseReady) {
      event.preventDefault();
      void finishTrial(undefined, 'anticipation');
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    const now = performance.now();
    const frame: TrainingFrameContext = {
      now,
      elapsed: now - phaseStartedAt.current,
      width,
      height,
      devicePixelRatio: window.devicePixelRatio || 1,
      pixelsPerDegree: calibration
        ? visualDegreesToPixels(1, calibration.viewingDistanceCm, calibration.pixelsPerCentimeter)
        : width / 90,
      calibration,
      selectedResponses: current.selected,
    };
    const response = module.getCanvasResponse(
      plan,
      frame,
      event.clientX - rect.left,
      event.clientY - rect.top,
      rect,
      current.selected,
    );
    if (!response) return;
    event.preventDefault();
    submitOption(response, now);
  }, [calibration, finishTrial, sequence, submitOption]);

  const abort = async () => {
    const current = uiRef.current;
    const module = sequence[current.moduleIndex];
    const partialResponse = current.selected.length && module
      ? serializeMulti(module, current.selected)
      : undefined;
    const currentTrial = current.plan
      && module
      && !resultsRef.current.some((trial) => trial.trialId === current.plan?.id)
      && pendingResult.current?.trialId !== current.plan.id
      && !['module-intro', 'feedback', 'saving', 'complete'].includes(current.phase)
      ? {
          id: createId('trial'),
          sessionId: sessionId.current,
          trialId: current.plan.id,
          moduleId: module.id,
          protocolVersion: current.plan.protocolVersion,
          seed: current.plan.seed,
          stimulusId: current.plan.stimulusId,
          startedAt: trialStartedAt.current,
          stimulusOnsetMs: stimulusOnsetAt.current,
          responseAtMs: firstSelectionAt.current === undefined ? undefined : performance.timeOrigin + firstSelectionAt.current,
          response: partialResponse,
          correctResponse: current.plan.correctResponse,
          correct: false,
          reactionTimeMs: firstSelectionAt.current === undefined || responseOnsetAt.current === 0
            ? undefined
            : firstSelectionAt.current - responseOnsetAt.current,
          difficulty: current.plan.difficulty,
          valid: false,
          invalidReason: invalidReason.current ?? 'session-aborted',
          environmentId: environment.id,
          metadata: {
            ...(module.getResultMetadata?.(current.plan, partialResponse) ?? {}),
            sessionMode: mode,
            assessment: isAssessmentMode(mode),
            firstSelectionLatencyMs: firstSelectionAt.current === undefined || responseOnsetAt.current === 0
              ? null
              : firstSelectionAt.current - responseOnsetAt.current,
          },
        } satisfies TrialResult
      : undefined;
    const withPending = pendingResult.current && !resultsRef.current.some((trial) => trial.id === pendingResult.current?.id)
      ? [...resultsRef.current, pendingResult.current]
      : resultsRef.current;
    const abortTrials = currentTrial ? [...withPending, currentTrial] : withPending;
    const session = createSessionRecord(
      sessionId.current, sessionStartedAt.current, mode, sequence.map(({ id }) => id),
      environment.id, getEnvironmentConditionId(environment), fatigue, correctionConfirmed, abortTrials, 'aborted',
    );
    try {
      await onAbort?.(session, abortTrials);
      onExit();
    } catch {
      setStorageError('中断状態を保存できませんでした。保存を再試行してください。');
      setUi((state) => ({ ...state, phase: 'paused', pauseReason: '中断記録の保存に失敗しました。' }));
    }
  };

  const retryPendingSave = async () => {
    const result = pendingResult.current;
    const module = result ? getTrainingModule(result.moduleId) : undefined;
    if (!result || !module) return;
    setStorageError(undefined);
    finishing.current = true;
    await persistResult(result, module);
  };

  const beginModule = async () => {
    if (!startPersisted) return;
    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
    } catch {
      setUi((state) => ({ ...state, pauseReason: '全画面表示を許可してください。' }));
      return;
    }
    if (!environmentStillMatches()) {
      setUi((state) => ({ ...state, pauseReason: '画面条件が開始時から変わっています。セッションを中止し、準備画面から再診断してください。' }));
      return;
    }
    startTrial(ui.moduleIndex, ui.trialIndex);
  };

  const options: readonly ResponseOption[] = ui.plan && activeModule
    ? activeModule.getResponseOptions(ui.plan, ui.selected).map(decorateResponseOption)
    : [];
  const positionedOptions = options.filter((option) => option.position);
  const panelOptions = activeModule?.getCanvasResponse
    ? []
    : options.filter((option) => !option.position);
  const responsePrompt = ui.plan && activeModule
    ? activeModule.getResponsePrompt?.(ui.plan, ui.selected) ?? '答えを選ぶ'
    : '答えを選ぶ';
  const feedback = ui.plan && ui.lastResult && activeModule
    ? activeModule.getFeedback(ui.plan, ui.lastResult.response, ui.lastResult.correct)
    : undefined;
  const selectionLimit = activeModule?.selectionCount ?? 3;
  const selectionComplete = ui.selected.length === selectionLimit;
  const canvasCanRespond = Boolean(
    ui.phase === 'active' && ui.responseReady && activeModule?.getCanvasResponse,
  );
  const clearPeripheralStimulus = Boolean(
    ui.phase === 'active' && !ui.responseReady && activeModule?.id === 'central-peripheral',
  );

  const responseButton = (option: ResponseOption, positioned = false) => {
    const selected = ui.selected.includes(option.value);
    return (
      <button
        key={option.value}
        type="button"
        className={`training-option-button${selected ? ' selected' : ''}${positioned ? ' positioned' : ''}`}
        style={positioned && option.position ? {
          left: `${option.position.xPercent}%`,
          top: `${option.position.yPercent}%`,
        } : undefined}
        aria-pressed={activeModule?.responseKind === 'multi-select' ? selected : undefined}
        aria-label={option.label}
        onClick={() => submitOption(option.value)}
      >
        <ResponseVisualIcon visual={option.visual} />
        <span className="training-option-text">{option.label}</span>
      </button>
    );
  };

  return (
    <main className="training-shell" aria-label="トレーニングセッション">
      <canvas
        ref={canvasRef}
        className={`training-canvas${canvasCanRespond ? ' response-interactive' : ''}`}
        aria-hidden="true"
        onPointerDown={answerFromCanvas}
      />
      <header className={`training-hud${clearPeripheralStimulus ? ' stimulus-clear' : ''}`}>
        <div><span className="training-brand">SCAN90</span><span className="training-module-name">{activeModule?.label}</span></div>
        <div className="training-progress-wrap"><span>{Math.min(overallTotal, overallTrial + 1)} / {overallTotal}</span><progress value={Math.min(overallTotal, overallTrial)} max={overallTotal} /></div>
        <button type="button" className="training-pause-button" onClick={() => pause('手動で一時停止しました。この試行は連続提示が途切れたため無効です。', true)}>一時停止</button>
      </header>

      {ui.phase === 'module-intro' && activeModule && (
        <section className="training-overlay training-intro" aria-labelledby="module-title">
          <div className="training-kicker">{sequence.length}課題中 {ui.moduleIndex + 1}つ目・{activeModule.evidenceLabel}</div>
          <h1 id="module-title">{activeModule.label}</h1>
          <p className="training-short-label">{activeModule.shortLabel}</p>
          <ol>{pointerFirstInstructions(activeModule).map((instruction) => <li key={instruction}>{instruction}</li>)}</ol>
          {(ui.pauseReason || storageError) && <p className="training-error" role="alert">{ui.pauseReason ?? storageError}</p>}
          {!startPersisted && storageError && <button className="training-secondary" type="button" onClick={() => void persistStart()}>保存を再試行</button>}
          <button className="training-primary" type="button" disabled={!startPersisted} onClick={() => void beginModule()}>{startPersisted ? (ui.moduleIndex === 0 && ui.trialIndex === 0 ? 'セッションを開始' : '続きから開始') : '開始記録を保存中…'}</button>
        </section>
      )}

      {ui.phase === 'saving' && (
        <section className="training-overlay training-pause" role="status">
          <div className="training-kicker">今回の記録</div><h1>結果を保存中</h1><p>ブラウザを閉じずにお待ちください。</p>
        </section>
      )}

      {ui.phase === 'active' && ui.responseReady && (
        <div className="training-response-layer" aria-live="polite">
          {positionedOptions.map((option) => responseButton(option, true))}
          <div className={`training-response-panel${positionedOptions.length ? ' positioned-mode' : ''}${activeModule?.getCanvasResponse ? ' canvas-mode' : ''}`}>
            <span className="training-response-label">{responsePrompt}</span>
            {activeModule?.getCanvasResponse && <small className="training-canvas-hint">画面上の点を直接選べます</small>}
            {panelOptions.length > 0 && <div className="training-options">{panelOptions.map((option) => responseButton(option))}</div>}
            {activeModule?.responseKind === 'multi-select' && !activeModule.autoSubmitMultiSelect && (
              <div className="training-selection-controls">
                <small>{ui.selected.length} / {selectionLimit} 選択</small>
                <button
                  className="training-confirm-button"
                  type="button"
                  disabled={!selectionComplete}
                  onClick={() => confirmSelections()}
                >選択を確定</button>
              </div>
            )}
          </div>
        </div>
      )}

      {ui.phase === 'feedback' && feedback && activeModule?.showTrainingFeedback !== false && (mode === 'training' || mode === 'familiarization') && (
        <section className={`training-feedback ${ui.lastResult?.correct ? 'correct' : 'incorrect'}`} aria-live="assertive">
          <span>{ui.lastResult?.valid ? (ui.lastResult.correct ? '一致' : '不一致') : '無効試行'}</span>
          <strong>{ui.lastResult?.valid ? feedback.title : '計測条件が崩れました'}</strong>
          <p>{ui.lastResult?.valid ? feedback.detail : `理由：${ui.lastResult?.invalidReason ?? '不明'}`}</p>
          <small>基準回答：{feedback.correctLabel}</small>
        </section>
      )}

      {ui.phase === 'paused' && (
        <section className="training-overlay training-pause" role="dialog" aria-modal="true" aria-labelledby="pause-title">
          <div className="training-kicker">トレーニングを停止しました</div><h1 id="pause-title">一時停止</h1><p>{ui.pauseReason}</p>
          {storageError && <p className="training-error" role="alert">{storageError}</p>}
          {invalidReason.current && <p className="training-error">現在の試行は「{invalidReason.current}」として無効になります。</p>}
          <div>
            {storageError ? (
              <button className="training-primary" type="button" onClick={() => {
                if (pendingResult.current) void retryPendingSave();
                else if (resultsRef.current.length >= overallTotal) void completeSession(resultsRef.current);
                else void abort();
              }}>保存を再試行</button>
            ) : <button className="training-primary" type="button" onClick={() => void resume()}>全画面で再開</button>}
            <button className="training-secondary" type="button" onClick={() => void abort()}>セッションを中止</button>
          </div>
        </section>
      )}

      <footer className={`training-footer${clearPeripheralStimulus ? ' stimulus-clear' : ''}`}><span>{ui.phase === 'fixation' ? '中央の十字を見てください' : ui.phase === 'active' && !ui.responseReady ? '刺激を見てください' : '右上のボタン、または Esc で一時停止'}</span><span>反応時間はブラウザ内で記録</span></footer>
    </main>
  );
}
